import * as THREE from 'three';
import {
  abovegroundPool,
  bigFlag,
  bugZapper,
  christmasLights,
  clawTub,
  clothesline,
  flamingo,
  gnome,
  inflatableGorilla,
  kiddiePool,
  lllSign,
  porchGoose,
  possum,
  satelliteDish,
  tirePlanter,
  trampoline,
  tvBox,
  type BuiltDecor,
} from './builders/decor';
import { oilDrumSmoker, recliner } from './builders/props';
import type { CollisionWorld } from './Collision';
import { homeSpots, type DecorSlot, type HomeFrame, type HomeKind } from './home';

/** Whoever owns the Camaro that Truck Nutz hang off. */
export interface TruckNutzHost {
  setTruckNutz(on: boolean): void;
}

interface Placed {
  object: THREE.Object3D;
  colliders: number[];
  update?: BuiltDecor['update'];
}

/** Keeps a home's 3D yard in sync with the decor its owner has. */
export class DecorManager {
  private readonly placed = new Map<string, Placed>();
  private slots: Record<string, DecorSlot>;
  private goose: ReturnType<typeof porchGoose> | null = null;
  private outfit = 0;
  trampolineMat: THREE.Mesh | null = null;

  constructor(
    private readonly car: TruckNutzHost,
    private readonly parent: THREE.Object3D,
    private readonly collision: CollisionWorld,
    frame: HomeFrame,
    kind: HomeKind = 'single',
  ) {
    this.slots = homeSpots(frame, kind).decor;
  }

  sync(owned: readonly string[]): void {
    for (const id of owned) if (!this.placed.has(id)) this.add(id);
    for (const id of [...this.placed.keys()]) if (!owned.includes(id)) this.remove(id);
  }

  /** Moves the whole yard to another lot, or rearranges it around a new double wide. */
  setFrame(frame: HomeFrame, kind: HomeKind = 'single'): void {
    const owned = [...this.placed.keys()];
    this.clear();
    this.slots = homeSpots(frame, kind).decor;
    this.sync(owned);
  }

  clear(): void {
    for (const id of [...this.placed.keys()]) this.remove(id);
  }

  setGooseOutfit(index: number): void {
    this.outfit = index;
    this.goose?.setOutfit(index);
  }

  update(time: number, dt: number, night: number): void {
    for (const p of this.placed.values()) p.update?.(time, dt, night);
  }

  private add(id: string): void {
    const slot = this.slots[id];
    if (!slot && id !== 'truckNutz') return;
    const colliders: number[] = [];
    let built: BuiltDecor;
    const c = this.collision;
    const s = slot as DecorSlot;

    switch (id) {
      case 'truckNutz':
        this.car.setTruckNutz(true);
        this.placed.set(id, { object: new THREE.Group(), colliders });
        return;
      case 'flamingos': {
        const g = new THREE.Group();
        const a = flamingo();
        const b = flamingo();
        b.position.set(0.8, 0, -0.3);
        b.rotation.y = 0.6;
        g.add(a, b);
        built = { group: g };
        break;
      }
      case 'gnomes': {
        const g = new THREE.Group();
        ['#c0392b', '#2b6cb0', '#2f8f3a'].forEach((hat, i) => {
          const gn = gnome(hat);
          gn.position.set(i * 0.55, 0, (i % 2) * 0.3);
          g.add(gn);
        });
        built = { group: g };
        break;
      }
      case 'goose': {
        this.goose = porchGoose();
        this.goose.setOutfit(this.outfit);
        built = this.goose;
        break;
      }
      case 'lll':
        built = { group: lllSign() };
        break;
      case 'tires':
        built = { group: tirePlanter() };
        colliders.push(c.addCircle(s.x, s.z, 0.55));
        break;
      case 'clothesline':
        built = clothesline();
        colliders.push(c.addCircle(s.x, s.z - 3, 0.12), c.addCircle(s.x, s.z + 3, 0.12));
        break;
      case 'flag':
        built = bigFlag();
        colliders.push(c.addCircle(s.x, s.z, 0.15));
        break;
      case 'lights':
        built = christmasLights(11.6);
        break;
      case 'zapper':
        built = bugZapper();
        break;
      case 'dish':
        built = { group: satelliteDish() };
        break;
      case 'kiddiePool':
        built = kiddiePool();
        break;
      case 'tub':
        built = clawTub();
        colliders.push(c.addRect(s.x, s.z, 1.7, 0.85));
        break;
      case 'smoker':
        built = { group: oilDrumSmoker() };
        colliders.push(c.addRect(s.x, s.z, 1.2, 0.75));
        break;
      case 'recliner':
        built = { group: recliner('#6b4a33') };
        colliders.push(c.addRect(s.x, s.z, 1.0, 1.0));
        break;
      case 'gorilla':
        built = inflatableGorilla();
        colliders.push(c.addCircle(s.x, s.z, 1.15));
        break;
      case 'trampoline': {
        const t = trampoline();
        this.trampolineMat = t.mat;
        built = t.built;
        colliders.push(c.addCircle(s.x, s.z, 1.95));
        break;
      }
      case 'pool':
        built = abovegroundPool();
        colliders.push(c.addCircle(s.x, s.z, 2.5));
        break;
      case 'bigTv':
        built = { group: tvBox() };
        colliders.push(c.addRect(s.x, s.z, 0.4, 1.9));
        break;
      case 'possum':
        built = possum();
        break;
      default:
        return;
    }

    const obj = built.group;
    obj.position.set(s.x, s.y ?? 0, s.z);
    obj.rotation.y = s.rot ?? 0;
    obj.traverse((o) => (o.userData.noBatch = true));
    this.parent.add(obj);
    this.placed.set(id, { object: obj, colliders, update: built.update });
  }

  private remove(id: string): void {
    const p = this.placed.get(id);
    if (!p) return;
    if (id === 'truckNutz') this.car.setTruckNutz(false);
    if (id === 'goose') this.goose = null;
    if (id === 'trampoline') this.trampolineMat = null;
    p.object.removeFromParent();
    p.object.traverse((o) => {
      if (o instanceof THREE.Mesh) o.geometry.dispose();
    });
    for (const cid of p.colliders) this.collision.remove(cid);
    this.placed.delete(id);
  }
}
